import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { format, isToday } from 'date-fns';
import dateFormat from 'date-fns/format';
import { has as _has } from 'lodash';
import * as uuid from 'uuid';

import { PaymentMethod, PaymentMethodUtilService } from '@amfam/billing/payment-method/util';
import { ScheduledPaymentActions } from '@amfam/billing/schedulepayment/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  AutomaticPayment,
  BillAccount,
  PaymentAccount,
  PaymentMethodRadioOption,
  ScheduledPayment,
  SchedulePaymentBillAccount,
  SchedulePaymentPayloadModel,
  SubmitScheduledPaymentPayloadModel,
  UpdateModeOfAuthModel
} from '@amfam/shared/models';
import { CurrencyUSDPipe, PrettyPaymentMethod } from '@amfam/shared/ui/pipes';
import {
  Applications,
  ApplicationService,
  CopyService
} from '@amfam/shared/utility/shared-services';
import { Option } from '@amfam/ui-kit';

import { OneTimePaySetup } from '../..';
import {
  AlertMessage,
  OTPAnalyticsModel,
  OTPReviewData
} from '../+state/one-time-pay-feature.models';

@Injectable({
  providedIn: 'root'
})
export class OneTimePayUtilService {
  isMyAccountAdmin: boolean;

  constructor(
    private paymentMethodUtil: PaymentMethodUtilService,
    private copyService: CopyService,
    private analyticsFacade: AnalyticsFacade,
    private applicationService: ApplicationService
  ) {
    this.isMyAccountAdmin = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);
  }
  /**
   * gets an array of Options objects for payment methods and generates proper sub helptexts
   * @param paymentMethods All payment Methods
   * @returns Payment Method Options array
   */
  getPaymentMethodOptions(paymentMethods: PaymentMethod[]): Option[] {
    return paymentMethods.map((paymentMethod: PaymentMethod) => {
      const option: Option = {
        text: paymentMethod?.nickName,
        helpText: [{ text: new PrettyPaymentMethod().transform(paymentMethod) }],
        id: paymentMethod?.paymentAccountId
      };
      if (this.paymentMethodUtil.isPaymentMethodExpired(paymentMethod)) {
        option.helpText.push({ text: 'Expired', warning: true });
      }
      if (this.paymentMethodUtil.isPaymentMethodExpiringSoon(paymentMethod)) {
        option.helpText.push({ text: 'Expiring soon', warning: true });
      }
      return option;
    });
  }

  buildPaymentMethodOptions(paymentMethods: PaymentMethod[]): PaymentMethodRadioOption[] {
    return paymentMethods.map(paymentMethod => {
      if (paymentMethod) {
        let lastFourOrThree;
        let icon;
        if (paymentMethod.creditCard) {
          const cardNumber = paymentMethod.creditCard.cardNumber;
          lastFourOrThree = cardNumber.substring(cardNumber.length - 4);
          icon = paymentMethod.creditCard.cardType;
        } else if (paymentMethod.achWithdrawal) {
          const accountNumber = paymentMethod.achWithdrawal.accountNumber;
          lastFourOrThree = accountNumber.substring(accountNumber.length - 3);
          icon = 'bank';
        }
        const option: PaymentMethodRadioOption = {
          nickName: paymentMethod.nickName,
          lastFourOrThree: lastFourOrThree,
          id: paymentMethod.paymentAccountId,
          icon: icon,
          bankIcon: paymentMethod.achWithdrawal ? true : false,
          expireStatus: null
        };
        if (this.paymentMethodUtil.isPaymentMethodExpired(paymentMethod)) {
          option.expireStatus = 'Expired';
        }
        if (this.paymentMethodUtil.isPaymentMethodExpiringSoon(paymentMethod)) {
          option.expireStatus = 'Expiring soon';
        }
        return option;
      } else {
        return null;
      }
    });
  }

  buildAlertMessage(
    payments: ScheduledPayment[],
    billAccounts: BillAccount[],
    autoPayAccounts: AutomaticPayment[],
    billAcc: string,
    amount: number
  ): AlertMessage {
    let title;

    // DP: Alertmessage hierarchy list as discussed:
    // 1. hasImmediateScheduledPayment
    // 2. hasMultipleScheduledPayments
    // 3. hasScheduledPayment
    // 4. hasPastDueLetterSent
    // 5. hasPastDue
    // 6. hasAutoPay
    // 7. hasLessThanMinDue

    // check if bill account has immediate/multiple/single scheduled payment
    const billAccountPayments: {
      billAccountNumber: string;
      billAcctPaymentAmount: string;
      receivedDate: string;
    }[] = [];
    payments.forEach(payment => {
      if (payment) {
        payment.billAccounts.forEach(acc => {
          if (acc.billAccountNumber === billAcc) {
            billAccountPayments.push({
              billAccountNumber: acc.billAccountNumber,
              billAcctPaymentAmount: acc.billAcctPaymentAmount,
              receivedDate: payment.receivedDate
            });
          }
        });
      }
    });

    if (billAccountPayments.length) {
      const isImmediatePayment = billAccountPayments.find(payment => isToday(payment.receivedDate));
      if (isImmediatePayment) {
        title = 'hasImmediateScheduledPayment';
        return this.getAlertMessageObject(title, billAcc);
      } else if (billAccountPayments.length > 1) {
        title = 'hasMultipleScheduledPayments';
        const body2Text = this.copyService.getCopy(
          `billing.oneTimePayRefactor.alertMessages.body2Text-manageOTP`
        );
        return this.getAlertMessageObject(title, billAcc, { body2Text });
      } else {
        title = 'hasScheduledPayment';
        const paymentAmount = new CurrencyUSDPipe().transform(
          billAccountPayments[0].billAcctPaymentAmount
        );
        const date = format(billAccountPayments[0].receivedDate, 'MM/DD/YYYY');
        const body2Text = this.copyService.getCopy(
          `billing.oneTimePayRefactor.alertMessages.body2Text-manageOTP`
        );
        return this.getAlertMessageObject(title, billAcc, { paymentAmount, date, body2Text });
      }
    }

    // check if bill account is past due or has PastDue Letter Sent
    const matchingBillAccount = billAccounts.find(acc => acc.billAccountNumber === billAcc);

    if (matchingBillAccount?.pastDue) {
      if (matchingBillAccount.cancelLetterSent) {
        title = 'hasPastDueLetterSent';
        const dueDate = format(matchingBillAccount.paymentDueDate, 'MM/DD/YYYY');
        const error = true;
        return this.getAlertMessageObject(title, billAcc, { dueDate, error });
      } else {
        title = 'hasPastDue';
        const dueDate = format(matchingBillAccount.paymentDueDate, 'MM/DD/YYYY');
        return this.getAlertMessageObject(title, billAcc, { dueDate });
      }
    }

    // check if bill account has autopay setup
    const matchingAutoPay = autoPayAccounts.find(ap => ap?.billAccountNumber === billAcc);
    if (matchingAutoPay) {
      title = 'hasAutoPay';
      const body2Text = this.copyService.getCopy(
        `billing.oneTimePayRefactor.alertMessages.body2Text-hasAutoPay`
      );
      const body3 = this.copyService.getCopy(
        `billing.oneTimePayRefactor.alertMessages.body3-hasAutoPay`
      );
      return this.getAlertMessageObject(title, billAcc, { body2Text, body3 });
    }

    // check if user trying to pay less than min due amount
    if (amount < Number(matchingBillAccount?.minimumAmountDue)) {
      title = 'hasLessThanMinDue';
      return this.getAlertMessageObject(title, billAcc);
    }
  }

  getAlertMessageObject(
    title: string,
    billAcc: string,
    bodyObj?: {
      date?: string;
      dueDate?: string;
      paymentAmount?: string;
      body2Text?: string;
      body3?: string;
      error?: boolean;
    }
  ): AlertMessage {
    const alertMessage = {
      title: title,
      heading: this.copyService.getCopy(
        `billing.oneTimePayRefactor.alertMessages.heading-${title}`
      ),
      body1: this.copyService.getCopy(`billing.oneTimePayRefactor.alertMessages.body1-${title}`, {
        date: bodyObj?.date,
        dueDate: bodyObj?.dueDate,
        billAccountNumber: billAcc,
        amount: bodyObj?.paymentAmount
      }),
      body2Text: bodyObj?.body2Text,
      body3: bodyObj?.body3,
      billAccountNumber: billAcc,
      error: bodyObj?.error ? bodyObj.error : false,
      warningText: this.copyService.getCopy(
        `billing.oneTimePayRefactor.alertMessages.warningText-${title}`
      )
    };
    return alertMessage;
  }

  buildOTPPayload(formData: {
    paymentAccount: PaymentAccount;
    paymentDate: string;
    billAccountList: BillAccount[];
    paymentAmountList: Dictionary<string>;
    totalPaymentAmount: number;
    scheduledPayment?: ScheduledPayment;
  }): SubmitScheduledPaymentPayloadModel {
    const outputDate = dateFormat(formData.paymentDate, 'YYYY-MM-DD');
    const paymentAccountType = _has(formData.paymentAccount, 'achWithdrawal') ? 'Bank' : 'Card';
    return {
      requestJson: this.buildOTPRequestJson(
        formData?.scheduledPayment?.paymentId || '',
        formData?.paymentAccount?.nickName,
        paymentAccountType,
        formData?.scheduledPayment?.lastUpdateTimestamp,
        outputDate,
        formData.totalPaymentAmount,
        formData.billAccountList,
        formData.paymentAmountList
      ),
      correlationId: uuid.v4(),
      paymentMethodType: paymentAccountType
    };
  }

  buildOTPRequestJson(
    scheduledPaymentId: string,
    paymentAccountNickName: string,
    paymentAccountType: 'Bank' | 'Card',
    lastUpdateTimestamp: string,
    paymentDate: string,
    totalPaymentAmount: number,
    billAccountList: BillAccount[],
    paymentAmount: Dictionary<string>
  ): SchedulePaymentPayloadModel {
    return {
      paymentId: scheduledPaymentId,
      lastUpdateTimestamp: lastUpdateTimestamp,
      payment: {
        billAccounts: this.buildOTPBillaccountRequestPayload({
          billAccountList: billAccountList,
          paymentAmount: paymentAmount
        }),
        paymentAccount: {
          nickName: paymentAccountNickName
        },
        paymentMethod: 'OLB',
        receivedDate: paymentDate,
        totalPaymentAmount:
          typeof totalPaymentAmount === 'number'
            ? totalPaymentAmount.toFixed(2)
            : totalPaymentAmount
      },
      paymentMethodType: paymentAccountType,
      correlationId: uuid.v4(),
      paymentTerm: this.buildOTPBillaccountRequestPayloadPaymentTerm({
        billAccountList: billAccountList,
        paymentAmount: paymentAmount
      })
    };
  }

  buildOTPBillaccountRequestPayload(billAccountRequestObj: {
    billAccountList: BillAccount[];
    paymentAmount: Dictionary<string>;
  }): SchedulePaymentBillAccount[] {
    const returnObjArr = [];
    for (const billaccObj of billAccountRequestObj.billAccountList) {
      returnObjArr.push({
        billAccountNumber: billaccObj?.billAccountNumber,
        billAccountNickName: billaccObj?.billingPreferences.accountNickName,
        billAcctPaymentAmount: Number(
          billAccountRequestObj.paymentAmount[billaccObj?.billAccountNumber]
        ).toFixed(2),
        pastDueInfo: {
          currentAmountDue: billaccObj?.minimumAmountDue as unknown as number,
          currentAmountDueDate: billaccObj?.paymentDueDate
        }
      });
    }
    return returnObjArr;
  }

  buildOTPBillaccountRequestPayloadPaymentTerm(billAccountRequestObj: {
    billAccountList: BillAccount[];
    paymentAmount: Dictionary<string>;
  }): string[] {
    const returnObjArr = [];
    for (const billaccObj of billAccountRequestObj.billAccountList) {
      if (
        billaccObj.minimumAmountDue ===
        billAccountRequestObj.paymentAmount[billaccObj?.billAccountNumber]
      ) {
        returnObjArr.push('minAmountDue');
      } else if (
        billaccObj.accountBalance ===
        billAccountRequestObj.paymentAmount[billaccObj?.billAccountNumber]
      ) {
        returnObjArr.push('unpaidBalance');
      } else {
        returnObjArr.push('otherAmount');
      }
    }
    return returnObjArr;
  }

  buildOTPPayloadforUpdateModeOfAuth(data: {
    paymentAccount: PaymentAccount;
    requestPayload: SubmitScheduledPaymentPayloadModel;
    isAdminAppWithoutModeOfAuthORAL: boolean;
    isNotAMyaccountAdminAppWithoutESIGN: boolean;
    action: 'add' | 'edit';
  }): UpdateModeOfAuthModel {
    const modeOfAuth = data.isAdminAppWithoutModeOfAuthORAL ? 'ORAL' : 'ESIGNATURE';
    const updateModeOfAuthPayload: UpdateModeOfAuthModel = {
      request: {
        lastUpdateTimestamp: data.paymentAccount.lastUpdateTimestamp,
        paymentAccount: {
          nickName: data?.paymentAccount?.nickName,
          modeOfAuthorization: modeOfAuth
        }
      },
      paymentAccountId: data?.paymentAccount?.paymentAccountId,
      paymentAction:
        data.action === 'add'
          ? [ScheduledPaymentActions.scheduledPaymentsSubmit(data.requestPayload)]
          : [ScheduledPaymentActions.scheduledPaymentsEdit(data.requestPayload.requestJson)],
      correlationId: uuid.v4()
    };
    return updateModeOfAuthPayload;
  }

  transformOTPentities(
    otpEntities: Dictionary<OneTimePaySetup>,
    paymentAccountEntities: Dictionary<PaymentAccount>,
    billAccountEntities: Dictionary<BillAccount>,
    scheduledPaymentEntities: Dictionary<ScheduledPayment>
  ) {
    let paymentAccount: PaymentAccount;
    let paymentDate: string;
    const billAccountList: BillAccount[] = [];
    const paymentAmountList: Dictionary<string> = {};
    let totalPaymentAmount = 0;
    let scheduledPayment: ScheduledPayment;

    // eslint-disable-next-line guard-for-in
    for (const billAccountNumber in otpEntities) {
      if (!paymentAccount) {
        paymentAccount =
          paymentAccountEntities[otpEntities[billAccountNumber].selectedPaymentAccount];
      }
      if (!paymentDate) {
        paymentDate = otpEntities[billAccountNumber].selectedPaymentDate;
      }
      if (!scheduledPayment) {
        scheduledPayment =
          scheduledPaymentEntities[otpEntities[billAccountNumber].scheduledPaymentId];
      }
      totalPaymentAmount =
        totalPaymentAmount +
        (otpEntities[billAccountNumber].selectedPaymentAmount as unknown as number);
      billAccountList.push(billAccountEntities[billAccountNumber]);
      paymentAmountList[billAccountNumber] = otpEntities[billAccountNumber]
        .selectedPaymentAmount as unknown as string;
    }

    return this.buildOTPPayload({
      paymentAccount,
      paymentDate,
      billAccountList,
      paymentAmountList,
      totalPaymentAmount,
      scheduledPayment
    });
  }

  buildEditOTPReviewData(
    paymentId: string,
    scheduledPaymentEntities: Dictionary<ScheduledPayment>
  ): OTPReviewData[] {
    const billAccountsInScheduledPayment = scheduledPaymentEntities[paymentId];
    const otpReviewData = [];
    for (const billAccount of billAccountsInScheduledPayment?.billAccounts ?? []) {
      otpReviewData.push({
        billAccountNumber: billAccount.billAccountNumber,
        selectedPaymentAccount: billAccountsInScheduledPayment.paymentAccount.nickName,
        selectedPaymentAmount: Number(billAccount.billAcctPaymentAmount),
        selectedPaymentDate: billAccountsInScheduledPayment?.receivedDate?.split('T')[0] || '',
        scheduledPaymentId: paymentId
      });
    }

    return otpReviewData;
  }

  /**
   * @author Abhishek Singh
   * @param analyticPayload: Payload for sending the analytics.
   * @returns
   * @description Send the analytics for the OTP Feature.
   */
  public sendOTPAnalytics(analyticPayload: OTPAnalyticsModel): void {
    const pageObj = {
      pageName: this.isMyAccountAdmin
        ? `MyAccount:Admin:BillingPayments:Payment:${analyticPayload.pageAnalyticName}`
        : `MyAccount:BillingPayments:Payment:${analyticPayload.pageAnalyticName}`,
      experience: '',
      primaryCategory: 'My Account',
      subCategory1: this.isMyAccountAdmin ? 'Admin' : 'Billing Payments',
      subCategory2: this.isMyAccountAdmin ? 'Billing Payments' : 'Payment',
      subCategory3: analyticPayload.pageAnalyticSubCategory3
        ? analyticPayload.pageAnalyticSubCategory3
        : '',
      paymentAmount: analyticPayload.paymentAmount,
      paymentDate: analyticPayload.paymentDate
    };
    if (analyticPayload.buttonAnalyticName) {
      return this.analyticsFacade.trackButtonClick({
        link: `MyAccount:BillingPayments:Payment:${analyticPayload.buttonAnalyticName}`
      });
    }

    return this.analyticsFacade.trackPage(pageObj);
  }
}
